import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Row } from 'reactstrap';
import get from 'lodash/get';
import { Field, reduxForm } from 'redux-form';
import isEmpty from 'lodash/isEmpty';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import Label from 'components/Input/Label';
import RenderedInput from 'components/RenderedInput';
import RenderedSelect from 'components/RenderedSelect';
import Well from 'components/Well';
import Button from 'components/Button';
import LargestCol from 'components/LargestCol';
import ReviewNote from 'components/ReviewNote';
import RenderedInputMask from 'components/RenderedInputMask';
import BackButtonContainer from 'containers/BackButtonContainer';
import ReservationDetailsContainer from 'containers/ReservationDetailsContainer';
import {
  BOOKING_ENGINE_PAGE_CHANGED,
  PAYMENT_METHODS,
  US_STATES_OPTIONS
} from 'utils/constants';
import { getFutureYearsOptions } from 'utils/dateUtils';
import {
  cardNumber,
  cvvCode,
  email,
  length,
  maxLength,
  phoneNumber,
  required
} from 'utils/validators';
import { getBookingEnginePath, HOST_BOOKING } from 'utils/routeUtils';
import { isMobileUserAgent } from 'utils/mobile';

const GuestCol = styled(LargestCol)`
  .row {
    margin-bottom: 0;
  }

  *[class*='col'] {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: 1em;
    position: relative;

    & > span {
      position: absolute;
      top: 100%;
      font-size: 0.8em;
    }
  }

  .header-col {
    margin-bottom: 0;
  }

  & > h1,
  & > h2,
  & > p {
    text-align: center;
  }

  & > h1,
  & > h2 {
    font-size: 1.5em;
    margin-bottom: 0;
  }

  .form-check.form-check-inline {
    .custom-control {
      margin-left: 0.5em;
    }
  }

  @media (min-width: ${props => props.theme.breakpoints.large}) {
    & > h1,
    & > h2,
    & > p {
      text-align: left;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.large}) {
    padding-bottom: 3em;
    max-width: ${props => props.theme.fullWidth};
  }
`;

const ReservationCol = styled(LargestCol)`
  @media (max-width: ${props => props.theme.breakpoints.large}) {
    max-width: ${props => props.theme.fullWidth};
    margin: ${props => props.theme.center};
  }
`;

const CreditInfoRow = styled(Row)`
  margin-bottom: 0;
`;

export class GuestInformation extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobile: false
    };
  }

  handleFormSubmit = values => {
    const {
      history: { push }
    } = this.props;

    push(getBookingEnginePath('review'));
  };

  componentDidMount() {
    const isMobile = isMobileUserAgent();
    const parentWindow = window.parent;

    if (isMobile) {
      this.handleMobileEvent(isMobile);
      window.addEventListener('mobile', this.handleMobileEvent);
    }

    parentWindow.postMessage(BOOKING_ENGINE_PAGE_CHANGED, '*');
  }

  componentWillUnmount() {
    if (window.removeEventListener) {
      window.removeEventListener('mobile', this.handleMobileEvent);
    }
  }

  handleMobileEvent = mobile => {
    const { isMobile } = this.state;

    if (isMobile !== mobile) {
      this.setState({
        isMobile: mobile
      });
    }
  };

  render() {
    const {
      submitting,
      handleSubmit,
      initialValues,
      selectedRoom,
      isAdmin,
      bookingSource
    } = this.props;

    const { isMobile } = this.state;

    const isHostBooking = bookingSource === HOST_BOOKING;
    const isHostBookingAdmin = isHostBooking && isAdmin;

    if (!selectedRoom) {
      return <Redirect to={getBookingEnginePath('choose-accommodation')} />;
    }

    return (
      <form onSubmit={handleSubmit(this.handleFormSubmit)}>
        <Helmet>
          <title>Guest Information</title>
        </Helmet>
        <Row>
          <GuestCol xs="12" lg={isHostBooking ? 5 : 7}>
            <h1>GUEST INFORMATION</h1>
            <p>
              You’re almost done! Fill out the following to complete your
              reservation.
            </p>
            <p>
              The name on the booking must match the name on the credit card.
            </p>
            <Row>
              <LargestCol xs="12" sm="6">
                <Row>
                  <LargestCol xs="12" className="header-col">
                    <h5>CONTACT INFORMATION</h5>
                  </LargestCol>
                </Row>
                <FormGroup row>
                  <LargestCol xs="12" sm="6">
                    <Label required htmlFor="firstName">
                      First Name
                    </Label>
                    <Field
                      id="firstName"
                      name="firstName"
                      component={RenderedInput}
                      placeholder="Jane"
                      validate={[required, maxLength(255)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin || !isEmpty(initialValues)
                          : !isEmpty(initialValues)
                      }
                    />
                  </LargestCol>
                  <LargestCol xs="12" sm="6">
                    <Label required htmlFor="lastName">
                      Last Name
                    </Label>
                    <Field
                      id="lastName"
                      name="lastName"
                      component={RenderedInput}
                      placeholder="Doe"
                      validate={[required, maxLength(255)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin || !isEmpty(initialValues)
                          : !isEmpty(initialValues)
                      }
                    />
                  </LargestCol>
                </FormGroup>
                <FormGroup row>
                  <LargestCol xs="12">
                    <Label required htmlFor="email">
                      Email Address
                    </Label>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      component={RenderedInput}
                      placeholder="janedoe@email.com"
                      validate={[required, email, maxLength(255)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues &&
                            !!initialValues.email &&
                            (typeof email(initialValues.email) ===
                              'undefined' &&
                              initialValues.email.length <= 255)
                      }
                    />
                  </LargestCol>
                </FormGroup>
                <FormGroup row>
                  <LargestCol xs="12" sm="7">
                    <Label required htmlFor="phone">
                      Phone Number
                    </Label>
                    <Field
                      id="phone"
                      name="phone"
                      component={RenderedInput}
                      placeholder="(555) 555-5555"
                      validate={[required, phoneNumber]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues &&
                            !!initialValues.phone &&
                            typeof phoneNumber(initialValues.phone) ===
                              'undefined'
                      }
                    />
                  </LargestCol>
                </FormGroup>
              </LargestCol>
              <LargestCol xs="12" sm="6">
                <Row>
                  <LargestCol xs="12" className="header-col">
                    <h5>BILLING ADDRESS</h5>
                  </LargestCol>
                </Row>
                <FormGroup row>
                  <LargestCol xs="12">
                    <Label required htmlFor="address">
                      Address
                    </Label>
                    <Field
                      id="address"
                      name="address"
                      component={RenderedInput}
                      placeholder="1234 Royal St., Suite 400"
                      validate={[required, maxLength(255)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues && !!initialValues.address
                      }
                    />
                  </LargestCol>
                </FormGroup>
                <FormGroup row>
                  <LargestCol xs="12">
                    <Label required htmlFor="city">
                      City
                    </Label>
                    <Field
                      id="city"
                      name="city"
                      component={RenderedInput}
                      placeholder="Mobile"
                      validate={[required, maxLength(255)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues && !!initialValues.city
                      }
                    />
                  </LargestCol>
                </FormGroup>
                <FormGroup row>
                  <LargestCol xs="7">
                    <Label required htmlFor="state">
                      State
                    </Label>
                    <Field
                      id="state"
                      name="state"
                      component={RenderedSelect}
                      validate={[required, maxLength(255)]}
                      options={US_STATES_OPTIONS}
                      inputProps={{ autoComplete: 'none' }}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues && !!initialValues.state
                      }
                    />
                  </LargestCol>
                  <LargestCol xs="5">
                    <Label required htmlFor="postalCode">
                      Zip Code
                    </Label>
                    <Field
                      id="postalCode"
                      name="postalCode"
                      component={RenderedInput}
                      placeholder="36526"
                      validate={[required, length(5)]}
                      disabled={
                        isHostBooking
                          ? !isHostBookingAdmin
                          : initialValues &&
                            !!initialValues.postalCode &&
                            initialValues.postalCode.length === 5
                      }
                    />
                  </LargestCol>
                </FormGroup>
              </LargestCol>
            </Row>
            <CreditInfoRow>
              <LargestCol xs="12">
                <Row>
                  <LargestCol xs="12" className="header-col">
                    <h5>CREDIT CARD INFORMATION</h5>
                  </LargestCol>
                </Row>
                <FormGroup row>
                  <LargestCol xs="12" sm="4">
                    <Label required htmlFor="cardType">
                      Card Type
                    </Label>
                    <Field
                      id="cardType"
                      name="cardType"
                      component={RenderedSelect}
                      validate={[required]}
                      options={PAYMENT_METHODS}
                      disabled={isHostBooking ? !isHostBookingAdmin : false}
                    />
                  </LargestCol>
                  <LargestCol xs="12" sm="6">
                    <Label required htmlFor="cardNumber">
                      Card Number
                    </Label>
                    <Field
                      id="cardNumber"
                      name="cardNumber"
                      component={RenderedInputMask}
                      placeholder="5555 5555 5555 5555"
                      validate={[required, cardNumber]}
                      disabled={isHostBooking ? !isHostBookingAdmin : false}
                    />
                  </LargestCol>
                  <LargestCol xs="4" sm="2">
                    <Label required htmlFor="cvv">
                      CVV
                    </Label>
                    <Field
                      id="cvv"
                      name="cvv"
                      component={RenderedInput}
                      placeholder="333"
                      validate={[required, cvvCode]}
                      disabled={isHostBooking ? !isHostBookingAdmin : false}
                    />
                  </LargestCol>
                  <LargestCol xs="4">
                    <Label required htmlFor="expirationMonth">
                      Expiration Month
                    </Label>
                    <Field
                      className="openUp"
                      id="expirationMonth"
                      name="expirationMonth"
                      component={RenderedSelect}
                      validate={[required]}
                      options={{
                        1: '01',
                        2: '02',
                        3: '03',
                        4: '04',
                        5: '05',
                        6: '06',
                        7: '07',
                        8: '08',
                        9: '09',
                        10: '10',
                        11: '11',
                        12: '12'
                      }}
                      disabled={isHostBooking ? !isHostBookingAdmin : false}
                    />
                  </LargestCol>
                  <LargestCol xs="4">
                    <Label required htmlFor="expirationYear">
                      Expiration Year
                    </Label>
                    <Field
                      className="openUp"
                      id="expirationYear"
                      name="expirationYear"
                      component={RenderedSelect}
                      validate={[required]}
                      options={getFutureYearsOptions(15)}
                      disabled={isHostBooking ? !isHostBookingAdmin : false}
                    />
                  </LargestCol>
                </FormGroup>
              </LargestCol>
            </CreditInfoRow>
            <BackButtonContainer
              className="back-button-middle"
              to={getBookingEnginePath('choose-accommodation')}
            />
          </GuestCol>
          <ReservationCol xs="12" lg={isHostBooking ? 4 : 5}>
            <Well>
              <ReservationDetailsContainer />
            </Well>
            <ReviewNote className="mb-3">
              For COMP hotel bookings, ROOM TYPE PREFERENCE is not guaranteed
              until check in.
            </ReviewNote>
            <Button type="submit" disabled={submitting} block secondary>
              Review &amp; Submit
            </Button>
            {isMobile && (
              <BackButtonContainer
                className="back-button-bottom"
                to={getBookingEnginePath('choose-accommodation')}
              />
            )}
            <ReviewNote>
              You will have an opportunity to review your information before
              confirming your booking.
            </ReviewNote>
          </ReservationCol>
          {!isMobile && (
            <LargestCol xs="12">
              <BackButtonContainer
                className="back-button-bottom"
                to={getBookingEnginePath('choose-accommodation')}
              />
            </LargestCol>
          )}
        </Row>
      </form>
    );
  }
}

GuestInformation.propTypes = {
  data: PropTypes.shape({
    hotelProfile: PropTypes.shape({
      operaProfile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        operaId: PropTypes.string,
        address: PropTypes.shape({
          addressLine: PropTypes.string,
          cityName: PropTypes.string,
          stateProv: PropTypes.string,
          countryCode: PropTypes.string,
          postalCode: PropTypes.string
        })
      }),
      patronBookingInfo: PropTypes.shape({
        patronAccountNumber: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        freePlay: PropTypes.number,
        shoppingDollars: PropTypes.number,
        basePoints: PropTypes.number,
        clubState: PropTypes.string,
        nextClubState: PropTypes.string,
        pointsNeeded: PropTypes.number,
        basePointsEarned: PropTypes.number,
        email: PropTypes.string,
        birthDate: PropTypes.string,
        rewardsJoinDate: PropTypes.string,
        homePhone: PropTypes.string,
        mobilePhone: PropTypes.string,
        gender: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        postalCode: PropTypes.string
      })
    })
  }),
  selectedRoom: PropTypes.object,
  property: PropTypes.string,
  isAdmin: PropTypes.bool,
  bookingSource: PropTypes.string,
  isWaitListBooking: PropTypes.bool
};

export default reduxForm({
  form: 'guestInfo',
  destroyOnUnmount: false
})(GuestInformation);
